import { Component, Input, OnDestroy, OnInit, forwardRef } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormArray, FormBuilder, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DepositAction, DepositsStatusEnum, EventModal, TabsDepositsEnum } from 'app/core/enums/tabs-deposits.enum';
import { FormatCurrencyHelper } from 'app/core/helpers/format-currency-helper';
import { CustomDate } from 'app/core/helpers/format-date-helper';
import { SweetAlertHelper } from 'app/core/helpers/sweet-alert-helper.service';
import { ToastrAlertHelper } from 'app/core/helpers/toastr-alert-helper-service';
import { IDepositDetail, IPushRow } from 'app/core/interfaces/deposit.interface';
import { IDepositDetailRow, IRowsTouched } from 'app/core/interfaces/rows-touched.interface';
import { ConciliationService } from 'app/core/services/conciliation.service';
import { T2LoadingService } from 'app/trip2-theme/components/t2-loading/t2-loading.service';
import { Observable, Subscription } from 'rxjs';
import { forkJoin } from 'rxjs/observable/forkJoin';

@Component({
  selector: 'app-deposits-details',
  templateUrl: './deposits-details.component.html',
  styleUrls: ['./deposits-details.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => DepositsDetailsComponent),
    multi: true
  }]
})
export class DepositsDetailsComponent implements OnInit, OnDestroy, ControlValueAccessor {

  @Input() heightArrayForm = '100%';
  detailform: FormGroup;
  subscriptions: Subscription[] = [];
  indexEditBtn = null;
  rowsTouched: IRowsTouched[] = [];
  depositCreditAmount = 0;
  depositDetailSaved = [];
  depositDetailNoSaved = [];
  headerSelectorRow = false;

  get depositidCtrl(): AbstractControl {
    return this.detailform.get('depositid');
  }

  get depositCtrl(): FormArray {
    return this.detailform.get('detail') as FormArray;
  }

  get totalAmountCtrl(): AbstractControl {
    return this.detailform.get('totalamount');
  }

  get totalAdjustmentCtrl(): AbstractControl {
    return this.detailform.get('totaladjustment');
  }

  get grandTotalCtrl(): AbstractControl {
    return this.detailform.get('grandtotal');
  }

  get totalPostedAmountCtrl(): AbstractControl {
    return this.detailform.get('total');
  }

  get diffAmountCtrl(): AbstractControl {
    return this.detailform.get('diff');
  }

  get totalSelectedRows(): number {
    return this.depositDetailSaved.length + this.depositDetailNoSaved.length;
  }

  get validateDiffValue(): boolean {
    let aux = this.diffAmountCtrl.value;
    if (this.diffAmountCtrl.value === 0) {
      aux = 0;
    }
    return (aux === 0);
  }

  onTouch: any = () => {};
  propagateChange = (_: any) => {};

  constructor(
    private fb: FormBuilder,
    private objServ: ConciliationService,
    private sweetAlertHelper: SweetAlertHelper,
    private t2LoadingService: T2LoadingService,
    private objToast: ToastrAlertHelper
  ) {
    this.createFormDetail();
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.detailform.get('detail').valueChanges
      .subscribe({
        next: (res) => {
          if (res && res.length) {
            this.propagateChange(this.depositCtrl.getRawValue());
          }
        },
        error: (er) => this.sweetAlertHelper.captureException(er)
      }),
      this.objServ.behaviorSubjDepositObs$
      .subscribe({
        next: (resp) => this.getTabEvent(resp),
        error: (er) => this.sweetAlertHelper.captureException(er)
      })
    );
  }

  writeValue(value: any): void {
    if (value) {
      if (value.depositid) {
        this.depositidCtrl.patchValue(value.depositid);
        this.depositCreditAmount = value.creditValue;
      } else {
        this.depositidCtrl.patchValue(null);
        this.depositCreditAmount = 0;
      }

      if (value.records && value.records.length) {
        const listBuild = this.buildReq(value.records);
        this.setFormArray(listBuild);
        this.getTotalCountDepositAmount(listBuild);
      } else if (value.pushRow) {
        this.autofillActionReq(value.pushRow);
      } else {
        this.clearRecords();
        this.getTotalCountDepositAmount();
      }
      this.indexEditBtn = null;
    } else {
      this.depositidCtrl.patchValue(null);
      this.clearRecords();
      this.getTotalCountDepositAmount();
    }
  }

  buildReq(arrayList: any[]): any[] {
    const bodyReqList = [];
    arrayList.forEach(row => {
      bodyReqList.push({
        checkboxRowGrid: false,
        adjustment: row.adjustment,
        amount: row.amount,
        checkdate: row.checkdate,
        checknbr: row.checknbr,
        depositid: row.depositid,
        id: row.id,
        payor: row.payor,
        total: row.posted ? row.posted.total.toFixed(2) : 0,
        diff: row.diff ? row.diff.toFixed(2) : 0,
        payorin: row.posted ? row.posted.payor : '',
        note: row.note
      });
    });
    return bodyReqList;
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  createFormDetail(): void {
    this.detailform = this.fb.group({
      totalamount: 0,
      totaladjustment: 0,
      grandtotal: 0,
      depositdetailadjustment: 0,
      depositdetailamount: 0,
      total: 0,
      diff: 0,
      depositid: null,
      detail: this.fb.array([])
    });
  }

  addRow(row = null): void {
    if (this.depositidCtrl.value) {
      const newGroup = this.createFormGroup({
        checkboxRowGrid: false,
        indexRow: this.generateRandomAlphanumeric(20),
        checkdate: row ? CustomDate.formatJs(row.checkdate, 'yyyy-MM-dd') : null,
        checknbr: row ? row.checknbr : null,
        amount: row ? row.amount : 0,
        adjustment: 0,
        payor: null,
        total: 0,
        diff: 0,
        payorin: null,
        depositid: this.depositidCtrl.value,
        note: null
      });
      this.depositCtrl.push(newGroup);
      this.propagateChange(this.depositCtrl.getRawValue());
      this.onTouch();
    } else {
      this.sweetAlertHelper.createCustomAlert({ title: 'Attention', type: 'error', text: 'Action not allowed' });
    }
  }

  setFormArray(details: any[]): void {
    this.clearRecords();
    details.forEach(detail => {
      const groupIterate = this.createFormGroup(detail);
      this.disableColumnsFormArray(groupIterate);
      this.depositCtrl.push(groupIterate);
    });
    this.propagateChange(this.depositCtrl.getRawValue());
    this.onTouch();
  }

  createFormGroup(rowValue: any): FormGroup {
    const group = this.fb.group({
      checkboxRowGrid: rowValue.checkboxRowGrid,
      indexRow: rowValue.indexRow ? rowValue.indexRow : null,
      adjustment: rowValue.adjustment,
      amount: rowValue.amount,
      checkdate: rowValue.checkdate ? CustomDate.formatJs(rowValue.checkdate, 'yyyy-MM-dd') : '',
      checknbr: rowValue.checknbr,
      depositid: rowValue.depositid,
      id: rowValue.id,
      payor: rowValue.payor,
      postedamount: rowValue.total,
      diff: rowValue.diff,
      payorin: rowValue.payorin,
      note: rowValue.note
    });
    return group;
  }

  private showAlert(fields: string[]): void {
    const formattedFields = fields.map(field => `<li>${field}</li>`).join('');
    this.sweetAlertHelper.createCustomAlert({
      title: 'Attention',
      text: 'Required fields are missing',
      type: 'error',
      html: `<h3 style='color: red'>Required Fields</h3>
      <div>
        <ul style='color: red; list-style: none;'>
          ${formattedFields}
        </ul>
      </div>`
    });
  }

  controlStatusFormGroup(): void {
    this.depositCtrl.controls.forEach((row: AbstractControl) => {
      if (row.get('id').value === null) {
        row.enable();
      } else {
        Object.keys(row.value).forEach((controlName) => {
          if (controlName !== 'id' && controlName !== 'checkboxRowGrid') {
            row.get(controlName).disable();
          }
        });
      }
    });
  }

  editRow(i: number): void {
    this.indexEditBtn = i;
    this.controlStatusFormGroup();
    this.depositCtrl.controls[i].enable();
    this.depositCtrl.updateValueAndValidity();
  }

  storeRow(i: number, item: FormGroup): void {
    const missingFields: string[] = [];

    if (!item.value.checkdate || item.value.checkdate.trim() === '') {
      missingFields.push('Check Date');
    }

    if (!item.value.checknbr || item.value.checknbr.trim() === '') {
      missingFields.push('Check Number');
    }

    if (missingFields.length) {
      this.showAlert(missingFields);
      return;
    }

    if (item && !missingFields.length) {
      this.t2LoadingService.show();
      const reqIn: IDepositDetail = {
        isactive: true,
        depositid: this.depositidCtrl.value,
        checknbr: item.value.checknbr,
        amount: Number(item.value.amount),
        adjustment: Number(item.value.adjustment),
        payor: item.value.payor,
        company: '',
        checkdate: item.value.checkdate,
        note: item.value.note
      };

      let objSubs$: Observable<any> = null;
      let txtMsg = '';

      if (item.value.id) {
        txtMsg = 'Deposit detail successfully updated';
        reqIn.id = item.value.id;
        objSubs$ = this.objServ.putDepositDetail(reqIn).finally(() => this.t2LoadingService.hiden());
      } else {
        txtMsg = 'Deposit successfully saved';
        objSubs$ = this.objServ.saveDepositDetail(reqIn).finally(() => this.t2LoadingService.hiden());
      }

      this.subscriptions.push(
        objSubs$.subscribe({
          next: (res) => {
            if (res) {
              this.removeRowTouched(i);
              this.objToast.success(txtMsg, '', { positionClass: 'toast-bottom-right' });
              this.t2LoadingService.show();
              let objSubsDetail$: Observable<any> = null;
              if (res.depositid) {
                objSubsDetail$ = this.objServ.getDetail(res.depositid).finally(() => this.t2LoadingService.hiden());
              } else {
                objSubsDetail$ = this.objServ.getDetail(this.depositidCtrl.value).finally(() => this.t2LoadingService.hiden());
              }

              this.subscriptions.push(
                objSubsDetail$.subscribe({
                  next: (resp) => {
                    const listBuild = this.buildReq(resp);
                    this.setFormArray(listBuild);
                    this.controlStatusFormGroup();
                    this.indexEditBtn = null;
                    this.getTotalCountDepositAmount();
                    this.rowsTouched = [];
                    this.resetCheckBoxControl();
                  },
                  error: (er) => this.sweetAlertHelper.captureException(er)
                })
              );
            }
          },
          error: (er) => this.sweetAlertHelper.captureException(er)
        })
      );
    } else {
      this.deleteRow(i);
    }
  }


  confirmRowDelete(i: number, item: FormGroup, rowSaved = true): void {
    if (rowSaved) {
      this.sweetAlertHelper.createCustomAlert({
        title: 'Confirm',
        text: 'Are you sure you want to delete this record? this action cannot be reversed.',
        type: 'warning',
        showCancelButton: true,
        cancelButtonColor: '#dc3545',
        confirmButtonColor: '#1464f6',
        confirmButtonText: 'Yes!',
        cancelButtonText: 'No!',
        focusCancel: true
      })
      .then((modalRes) => {
        if (modalRes.value) {
          this.t2LoadingService.show();
          this.subscriptions.push(
            this.objServ.depositDetailDeleteAll([item.value.id])
            .finally(() => this.removeRowTouched(item.value.id))
            .subscribe({
              next: (res) => {
                if (res && res.message) {
                  this.deleteRow(i);
                  this.getTotalCountDepositAmount();
                  this.t2LoadingService.hiden();
                  this.objToast.success(res.message, '', { positionClass: 'toast-bottom-right' });
                  this.resetCheckBoxControl();
                }
              },
              error: (er) => this.sweetAlertHelper.captureException(er)
            })
          );
        } else {
          this.controlStatusFormGroup();
          this.indexEditBtn = null;
        }
      });
    } else {
      this.deleteRow(i);
      this.getTotalCountDepositAmount();
      const guidKey = item.get('indexRow').value;
      this.removeRowTouched(guidKey);
      this.deleteSelectedRowNoSaved(guidKey);
    }
  }

  deleteRow(i: number): void {
    this.depositCtrl.removeAt(i);
    this.propagateChange(this.depositCtrl.getRawValue());
    this.onTouch();
  }

  clearRecords(): void {
    while (this.depositCtrl.controls.length !== 0) {
      this.depositCtrl.removeAt(0);
    }
    this.propagateChange(this.depositCtrl.getRawValue());
    this.onTouch();
  }

  calculateDiff(index: number): void {
    const controls = this.depositCtrl.controls[index];
    const amountRow = parseFloat(controls.get('amount').value) || 0;
    const postedamountRow = parseFloat(controls.get('postedamount').value) || 0;
    const diff = (amountRow - postedamountRow).toFixed(2);
    controls.get('diff').patchValue(diff);
    this.depositCtrl.updateValueAndValidity();
    this.getTotalCountDepositAmount();
  }

  getPostedByCheckNumber(item: FormGroup, resetAll = true, hasAutoFill = false, pushRow: IPushRow = null): void {
    const depositAmountGrid = item.get('amount').value;
    const checkNumber = item.get('checknbr').value;
    if (checkNumber !== null && checkNumber.trim() !== '' && this.depositidCtrl.value) {
      this.subscriptions.push(
        this.objServ.getPostedByCheckNumber(checkNumber, this.depositidCtrl.value)
        .subscribe({
          next: (re) => {
            if (re && re.checknumber) {
              const postedAmount = re.total.toFixed(2);
              const payorin = re.payor;

              item.get('postedamount').patchValue(postedAmount);
              item.get('payorin').patchValue(payorin);
              item.get('payor').patchValue(payorin);
              const amountRow = parseFloat(item.get('amount').value) || 0;
              const diff = (amountRow - postedAmount).toFixed(2);
              item.get('diff').patchValue(diff);
              this.getTotalCountDepositAmount();
              if (hasAutoFill && pushRow) {
                this.autofillSavedetail(pushRow, payorin);
              }
              if (payorin) { this.rowNoSaved(item); }
            } else {
              this.rowResetCounts(item);
            }
          },
          error: (er) => {
            this.objToast.error(er.message, '', { positionClass: 'toast-bottom-right' });
            if (!isNaN(Number(depositAmountGrid)) && Number(depositAmountGrid) === 0) {
              this.rowResetCounts(item, resetAll);
            }
            if (pushRow) {
              this.autofillSavedetail(pushRow);
            } else {
              item.get('payorin').patchValue('Not Found');
              item.get('payor').patchValue('Not Found');
              item.get('postedamount').patchValue(0);
              item.get('diff').patchValue(item.get('amount').value);
              this.getTotalCountDepositAmount();
              this.rowNoSaved(item);
            }
          }
        })
      );
    } else {
      if (!isNaN(Number(depositAmountGrid)) && Number(depositAmountGrid) === 0) {
        this.rowResetCounts(item);
      }
    }
  }

  rowResetCounts(item: FormGroup, resetAll = true): void {
    item.get('postedamount').patchValue(0);
    item.get('payorin').patchValue('Not Found');
    item.get('payor').patchValue('Not Found');
    item.get('adjustment').patchValue(0);
    if (resetAll) {
      item.get('diff').patchValue(0);
      item.get('amount').patchValue(0);
    }
    this.getTotalCountDepositAmount();
  }

  rowEditMode(item: AbstractControl, indexEditBtn: number, i: number): boolean {
    return item.value.id && indexEditBtn === i;
  }

  refreshGrid(): void {
    this.sweetAlertHelper.createCustomAlert({
      title: 'Confirm',
      text: 'Are you sure you want to reset changes? Unsaved changes will be lost.',
      type: 'warning',
      showCancelButton: true,
      cancelButtonColor: '#dc3545',
      confirmButtonColor: '#1464f6',
      confirmButtonText: 'Yes!',
      cancelButtonText: 'No!'
    })
    .then((res) => {
      if (res && res.value) {
        this.t2LoadingService.show();
        this.execGetDetailAndResetRowsTouched();
      }
    });
  }

  getTotalCountDepositAmount(arrayListParam?: any[]): void {
    const arrayList = arrayListParam && arrayListParam.length ? arrayListParam : this.depositCtrl.getRawValue();
    let countAmount = 0;
    let countAdjustment = 0;
    let countPostedAmount = 0;

    if (arrayList.length) {
      arrayList.forEach(row => {
        countAmount += isNaN(Number(row.amount)) ? 0 : Number(row.amount);
        countAdjustment += isNaN(Number(row.adjustment)) ? 0 : Number(row.adjustment);
        countPostedAmount += isNaN(Number(row.total)) ? 0 : Number(row.total);
        countPostedAmount += isNaN(Number(row.postedamount)) ? 0 : Number(row.postedamount);
      });
    }

    this.totalAmountCtrl.patchValue(Number(countAmount.toFixed(2)));
    this.totalAdjustmentCtrl.patchValue(Number(countAdjustment.toFixed(2)));
    this.totalPostedAmountCtrl.patchValue(Number(countPostedAmount.toFixed(2)));

    const grandTotal = Number(countAmount + countAdjustment).toFixed(2);
    this.grandTotalCtrl.patchValue(grandTotal);

    const diffResult = this.depositCreditAmount - ( Number(countPostedAmount.toFixed(2)) + Number(countAdjustment.toFixed(2)));
    this.diffAmountCtrl.patchValue(diffResult);
  }

  resetCounts(): void {
    this.totalAmountCtrl.patchValue(0);
    this.totalAdjustmentCtrl.patchValue(0);
    this.grandTotalCtrl.patchValue(0);
    this.totalPostedAmountCtrl.patchValue(0);
  }

  formatCurrency(value: string): string {
    const formatVal = isNaN(Number(value)) ? 0 : Number(value);
    return FormatCurrencyHelper.formatCurrency(formatVal);
  }

  validateRow(param: IRowsTouched): void {
    if (param && param.action && param.row &&
      param.row.checkdate !== null && param.row.checkdate.trim() !== '' &&
      param.row.checknbr !== null && param.row.checknbr.trim() !== ''
    ) {
      if (param.row) {
        const rowMatch = this.rowsTouched.find(f => f.indexRow === param.indexRow);
        if (rowMatch) {
          rowMatch.row = param.row;
        } else {
          const exists = this.rowsTouched.some(item => item.indexRow === param.indexRow);
          if (!exists) {
            this.rowsTouched.push({ action: param.action, indexRow: param.indexRow, row: param.row });
          }
        }
      }
    } else {
      this.removeRowTouched(param.indexRow);
    }
  }

  removeRowTouched(indexRow: any): void {
    const index = this.rowsTouched.findIndex(obj => obj.indexRow === indexRow);
    if (index !== -1) {
      this.rowsTouched.splice(index, 1);
    }
  }

  rowNoSaved(row: FormGroup): void {
    const touchedRow = row.getRawValue();
    const idexRowInserted = touchedRow.indexRow || touchedRow.id;
    const actionName = touchedRow.id ? DepositAction.Update : DepositAction.Save;
    this.validateRow({ action: actionName, indexRow: idexRowInserted, row: touchedRow });
  }

  getTabEvent(resp: { action: string, data: any }): void {
    if (resp && resp.action === DepositAction.ChangeTab && resp.data.value) {
      switch (resp.data.value) {
        case TabsDepositsEnum.Deposits:
          if (this.rowsTouched && this.rowsTouched.length) {
            this.sweetAlertHelper.createCustomAlert(
              {
                title: 'Confirm',
                text: 'You have unsaved changes. what do you want to do?',
                type: 'warning',
                showCloseButton: true,
                showCancelButton: true,
                cancelButtonColor: '#dc3545',
                confirmButtonColor: '#1464f6',
                confirmButtonText: 'Exit and save',
                cancelButtonText: 'Exit without saving'
              }
            )
            .then((resModal: any) => {
              if (resModal && resModal.value) {
                this.saveCommit();
              } else {
                switch (resModal.dismiss) {
                  case EventModal.Cancel:
                    this.rowsTouched = [];
                    this.getDetail();
                    this.returnEventTab(DepositAction.ChangeTabFromDetail, resp.data.value);
                    break;
                  case EventModal.Close:
                  case EventModal.Overlay:
                    break;
                  default:
                    break;
                }
              }
            });
          } else {
            this.returnEventTab(DepositAction.ChangeTabFromDetail, resp.data.value);
          }
          break;
        default:
          break;
      }
    } else if (resp && resp.action === DepositAction.BulkSave && resp.data.value) {
      switch (resp.data.value) {
        case TabsDepositsEnum.Detail:
          if (this.rowsTouched && this.rowsTouched.length) {
            this.sweetAlertHelper.createCustomAlert(
              {
                title: 'Confirm',
                text: 'You have unsaved changes. what do you want to do?',
                type: 'warning',
                showCloseButton: true,
                showCancelButton: true,
                cancelButtonColor: '#dc3545',
                confirmButtonColor: '#1464f6',
                confirmButtonText: 'Save',
                cancelButtonText: 'Cancel'
              }
            ).then((re) => {
              if (re.value) {
                if (this.rowsTouched.length) {
                  this.saveCommit(false);
                }
              }
            });
          } else {
            this.objToast.error('Not pending changes in details', 'Save All Deposit Details', { positionClass: 'toast-bottom-right' });
          }
          break;
        default:
          break;
      }
    } else if (resp && resp.data && resp.data.value && resp.action === DepositAction.StepperActionButton) {
      if (this.rowsTouched && this.rowsTouched.length) {
        this.sweetAlertHelper.createCustomAlert(
          {
            title: 'Confirm',
            text: 'You have unsaved changes. what do you want to do?',
            type: 'warning',
            showCloseButton: true,
            showCancelButton: true,
            cancelButtonColor: '#dc3545',
            confirmButtonColor: '#1464f6',
            confirmButtonText: 'Continue and save',
            cancelButtonText: 'Continue without saving'
          }
        ).then((re: any) => {
          if (re && re.value) {
            if (this.rowsTouched.length) {
              this.returnEventTab(DepositAction.StepperActionButtonResp, {
                nextDeposit: true,
                rowCountTouched: this.rowsTouched.length,
                stepperNumber: resp.data.value.stepperNumber
              });
              this.saveCommitStepper();
            }
          } else {
            switch (re.dismiss) {
              case EventModal.Cancel:
                this.rowsTouched = [];
                this.returnEventTab(DepositAction.StepperActionButtonResp, {
                  nextDeposit: true,
                  rowCountTouched: 0,
                  stepperNumber: resp.data.value.stepperNumber
                });
                break;
              case EventModal.Close:
              case EventModal.Overlay:
                break;
              default:
                break;
            }
          }
        });
      } else {
        this.returnEventTab(DepositAction.StepperActionButtonResp, {
          nextDeposit: true,
          rowCountTouched: this.rowsTouched.length,
          stepperNumber: resp.data.value.stepperNumber
        });
      }
      this.resetCheckBoxControl();
    } else if (resp && resp.data && resp.data.value &&
                resp.data.value.depositid &&
                resp.data.value.pushRow &&
                resp.data.value.currentStatusId &&
                resp.action === DepositAction.AutoFillActionButton) {
      const pushRow = resp.data.value.pushRow;
      this.autofillActionReq(pushRow);
    } else if (resp && resp.action === DepositAction.UpdateGridAfterCloseCopyPasteModal) {
      this.t2LoadingService.show();
      this.getDetail({ execAutoStatusChange: true });
    } else if (resp && resp.action === DepositAction.AutoFillDetailPayor) {
      this.t2LoadingService.show();
      this.autofillPayorDetailActionReq(resp.data.value.depositId);
    } else if (resp && resp.action === DepositAction.ChangeTabFromConciliation && resp.data.detail && resp.data.deposit) {
      const deposit = resp.data.deposit;
      this.depositidCtrl.patchValue(deposit.id);
      if (deposit.id) {
        this.execGetDetailAndResetRowsTouched(deposit.id);
      }
    }
  }

  returnEventTab(actionName: string, value: any): void {
    this.objServ.behaviorSubjDeposit.next({ action: actionName, data: { value } });
  }

  getDetail(callback: { deposiId?: number, execAutoStatusChange?: boolean } = null): void {
    const depositId = callback && callback.deposiId ? callback.deposiId : this.depositidCtrl.value;
    this.subscriptions.push(
      this.objServ.getDetail(depositId)
      .subscribe({
        next: (resp) => {
          const listBuild = this.buildReq(resp);
          this.setFormArray(listBuild);
          this.indexEditBtn = null;
          this.getTotalCountDepositAmount();
          this.t2LoadingService.hiden();
          if (callback && callback.execAutoStatusChange) {
            this.autoStatusChange();
          }
          this.resetCheckBoxControl();
        },
        error: (er) => {
          this.t2LoadingService.hiden();
          this.sweetAlertHelper.captureException(er);
        }
      })
    );
  }

  buildBulkReq(row: IDepositDetailRow): IDepositDetail {
    const reqIn: IDepositDetail = {
      id: row.id,
      isactive: true,
      depositid: this.depositidCtrl.value,
      checknbr: row.checknbr,
      amount: Number(row.amount),
      adjustment: Number(row.adjustment),
      payor: row.payor,
      company: '',
      checkdate: row.checkdate,
      note: row.note
    };

    return reqIn;
  }

  bulkSaveAndUpdate(saveList: IRowsTouched[], updateList: IRowsTouched[], tabChange = true): void {
    if (saveList && saveList.length) {
      const obsInsert$: Observable<any>[] = saveList.map(rowS => {
        const reqIn = this.buildBulkReq(rowS.row);
        return this.objServ.saveDepositDetail(reqIn).finally(() => this.t2LoadingService.hiden());
      });

      this.subscriptions.push(
        forkJoin(obsInsert$).subscribe({
          next: (re: any) => {
            if (re && re.length) {
              const checknbr = re.map((resp: any) => resp.checknbr).join(', ');
              this.objToast.success(`Check number ${checknbr} saved successfully`, '', { positionClass: 'toast-bottom-right' } );
              this.rowsTouched = [];
              if (tabChange) {
                this.returnToTabByName(TabsDepositsEnum.Deposits);
              }
              this.getDetail();
            }
          },
          error: (er) => this.sweetAlertHelper.captureException(er)
        })
      );
    }

    if (updateList && updateList.length) {
      const obsUpdate$: Observable<any>[] = updateList.map(rowU => {
        const reqUp = this.buildBulkReq(rowU.row);
        return this.objServ.putDepositDetail(reqUp).finally(() => this.t2LoadingService.hiden());
      });

      this.subscriptions.push(
        forkJoin(obsUpdate$).subscribe({
          next: (re: any) => {
            if (re && re.length) {
              const messageTxt = re.map((resp: any) => resp.message).join(', ');
              this.objToast.success(messageTxt, '', { positionClass: 'toast-bottom-right' });
              this.rowsTouched = [];
              if (tabChange) {
                this.returnToTabByName(TabsDepositsEnum.Deposits);
              }
              this.getDetail();
            }
          },
          error: (er) => this.sweetAlertHelper.captureException(er)
        })
      );
    }
  }

  returnToTabByName(tabNumber: TabsDepositsEnum): void {
    this.returnEventTab(DepositAction.ChangeTabFromDetail, tabNumber);
  }

  saveCommit(tabChangeAction = true): void {
    const rowsToSave = this.rowsTouched.filter(rs => rs.action === DepositAction.Save);
    const rowsToUpdate = this.rowsTouched.filter(ru => ru.action === DepositAction.Update);
    this.bulkSaveAndUpdate(rowsToSave, rowsToUpdate, tabChangeAction);
  }

  saveCommitStepper(): void {
    const rowsToSave = this.rowsTouched.filter(rs => rs.action === DepositAction.Save);
    const rowsToUpdate = this.rowsTouched.filter(ru => ru.action === DepositAction.Update);

    if (rowsToSave && rowsToSave.length) {
      const obsInsert$: Observable<any>[] = rowsToSave.map(rowS => {
        const reqIn = this.buildBulkReq(rowS.row);
        return this.objServ.saveDepositDetail(reqIn).finally(() => this.t2LoadingService.hiden());
      });

      this.subscriptions.push(
        forkJoin(obsInsert$).subscribe({
          next: (re: any) => {
            if (re && re.length) {
              const checknbr = re.map((resp: any) => resp.checknbr).join(', ');
              this.objToast.success(`Check number ${checknbr} saved successfully.`, '', { positionClass: 'toast-bottom-right' });
              this.rowsTouched = [];
            }
          },
          error: (er) => this.sweetAlertHelper.captureException(er)
        })
      );
    }

    if (rowsToUpdate && rowsToUpdate.length) {
      const obsUpdate$: Observable<any>[] = rowsToUpdate.map(rowU => {
        const reqUp = this.buildBulkReq(rowU.row);
        return this.objServ.putDepositDetail(reqUp).finally(() => this.t2LoadingService.hiden());
      });

      this.subscriptions.push(
        forkJoin(obsUpdate$).subscribe({
          next: (re: any) => {
            if (re && re.length) {
              const messageTxt = re.map((resp: any) => resp.message).join(', ');
              this.objToast.success(messageTxt, '', { positionClass: 'toast-bottom-right' });
              this.rowsTouched = [];
            }
          },
          error: (er) => this.sweetAlertHelper.captureException(er)
        })
      );
    }
  }

  generateRandomAlphanumeric(length: number) {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
        const randomIndex = Math.floor(Math.random() * characters.length);
        result += characters[randomIndex];
    }
    return result;
  }

  autofillActionReq(input: { checkdate: any, checknbr: any, amount: any }): void {
    const pushRow = {
      checkdate: input.checkdate,
      checknbr: input.checknbr,
      amount: input.amount
    };

    this.addRow(pushRow);
    const lastIndex = this.depositCtrl.controls.length - 1;
    const lastRowControl = this.depositCtrl.at(lastIndex) as FormGroup;

    this.getPostedByCheckNumber(lastRowControl, false, true, pushRow);
    this.calculateDiff(lastIndex);
  }

  autofillPayorDetailActionReq(depositId: number): void {
    this.objServ.refreshDetailsPayor(depositId)
    .subscribe({
      next: () => this.getDetail(),
      error: err => this.sweetAlertHelper.captureException(err)
    });
  }

  autoStatusChange(): void {
    const diffAmount = this.diffAmountCtrl.value;
    if (diffAmount === 0) {
      this.returnEventTab(DepositAction.AutoFillActionButtonResp, { statusId: DepositsStatusEnum.Completed });
    } else {
      this.returnEventTab(DepositAction.AutoFillActionButtonResp, { statusId: DepositsStatusEnum.NotFound });
    }
  }

  autofillSavedetail(pushRow: IPushRow, payor = 'Not Found'): void {
    if (pushRow) {
      const reqIn: IDepositDetail = {
        isactive: true,
        depositid: this.depositidCtrl.value,
        checknbr: pushRow.checknbr,
        amount: Number(pushRow.amount),
        adjustment: 0,
        payor,
        company: '',
        checkdate: pushRow.checkdate,
        note: null
      };

      this.objServ.saveDepositDetail(reqIn).finally(() => this.t2LoadingService.hiden())
      .subscribe({
        next: (res) => {
          if (res) {
            this.objToast.success('Deposit successfully saved', 'Auto Save', { positionClass: 'toast-bottom-right' });
            this.t2LoadingService.show();
            this.indexEditBtn = null;
            this.rowsTouched = [];
            this.autoStatusChange();
            this.getTotalCountDepositAmount();
            this.controlStatusFormGroup();

            if (this.depositidCtrl.value) {
              this.getDetail();
            } else {
              this.sweetAlertHelper.createCustomAlert({ title: 'Attention', type: 'error', text: 'Action not allowed' });
            }
          }
        },
        error: (er) => this.sweetAlertHelper.captureException(er)
      });
    }
  }

  selectAllRows(event): void {
    const checkbox = event.target as HTMLInputElement;
    if (checkbox.checked) {
      this.depositDetailSaved = this.depositCtrl.controls.map(row => row.get('id').value).filter(row => row !== null);
      this.depositDetailNoSaved = this.depositCtrl.controls.map(row => row.get('indexRow').value).filter(row => row !== null);
      this.depositCtrl.controls.forEach(row => row.get('checkboxRowGrid').patchValue(true));
    } else {
      this.resetCheckBoxControl();
    }
  }

  rowSelected(item: FormGroup): void {
    if (item.get('id').value) {
      this.toggleNumberOrKeyInArray(item.get('id').value);
    } else {
      this.toggleNumberOrKeyInArray(item.get('indexRow').value, false);
    }
    if (this.totalSelectedRows === 0) {
      this.headerSelectorRow = false;
    }
  }

  bulkDelete(): void {
    if (this.depositDetailSaved && this.depositDetailSaved.length) {
      this.t2LoadingService.show();
      this.objServ.depositDetailDeleteAll(this.depositDetailSaved)
      .finally(() => this.t2LoadingService.hiden())
      .subscribe({
        next: (resp) => {
          if (resp && resp.message && resp.data) {
            this.objToast.success(`${resp.message}. Rows deleted: ${resp.data.length}`,
              'Delete All', { positionClass: 'toast-bottom-right' });
              this.execGetDetailAndResetRowsTouched();
              this.depositDetailSaved = [];
          }
        },
        error: (er) => this.sweetAlertHelper.captureException(er)
      });
    }

    if (this.depositDetailNoSaved && this.depositDetailNoSaved.length) {
      this.depositDetailNoSaved.forEach(row => {
        const index = this.depositCtrl.controls.findIndex(rowControl => rowControl.get('indexRow').value === row);
        this.deleteRow(index);
      });
      this.depositDetailNoSaved = [];
    }
  }

  toggleNumberOrKeyInArray(valueKey: any, savedRow = true) {
    if (savedRow) {
      const index = this.depositDetailSaved.indexOf(valueKey);
      if (index === -1) {
        this.depositDetailSaved.push(valueKey);
      } else {
        this.depositDetailSaved.splice(index, 1);
      }
    } else {
      const index = this.depositDetailNoSaved.indexOf(valueKey);
      if (index === -1) {
        this.depositDetailNoSaved.push(valueKey);
      } else {
        this.depositDetailNoSaved.splice(index, 1);
      }
    }
  }

  deleteSelectedRowNoSaved(guidKey): void {
    const index = this.depositDetailNoSaved.indexOf(guidKey);
    if (index !== -1) {
      this.depositDetailNoSaved.splice(index, 1);
    }
  }

  resetCheckBoxControl(): void {
    this.headerSelectorRow = false;
    this.depositDetailSaved = [];
    this.depositDetailNoSaved = [];
    this.depositCtrl.controls.forEach(row => row.get('checkboxRowGrid').patchValue(false));
  }

  execGetDetailAndResetRowsTouched(depositId = null): void {
    const depositIdVal = depositId ? depositId : this.depositidCtrl.value;
    if (depositIdVal) {
      this.rowsTouched = [];
      this.getDetail({ deposiId: depositIdVal });
    } else {
      this.sweetAlertHelper.createCustomAlert({ title: 'Attention', type: 'error', text: 'Action not allowed' });
    }
  }

  getIdRow(item: FormGroup): any {
    return item.get('id').value ? item.get('id').value : item.get('indexRow').value;
  }

  disableColumnsFormArray(groupIterate: FormGroup): void {
    Object.keys(groupIterate.controls).forEach(controlName => {
      if (controlName !== 'id' && controlName !== 'checkboxRowGrid') {
        groupIterate.get(controlName).disable();
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) =>
      subscription.unsubscribe()
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/billing-general/deposits/deposits-details/deposits-details.component.ts